// Utility functions for entity API operations

// Type for Supabase query builders to allow method calls
// Using unknown to maintain type safety while avoiding complex generic constraints
export type SupabaseQuery = unknown;

// Type for the result of awaiting a Supabase query
export interface SupabaseQueryResult {
  data: unknown[] | null;
  error: unknown | null;
  count?: number | null;
}

/**
 * Parse order by string to extract field and direction
 * @param orderBy - Order by string (e.g., "-created_at")
 * @returns Object with field and ascending properties
 */
export const parseOrderBy = (orderBy = '-created_at'): { field: string; ascending: boolean } => {
  const field = orderBy.startsWith('-') ? orderBy.slice(1) : orderBy;
  const ascending = !orderBy.startsWith('-');
  return { field, ascending };
};

/**
 * Apply filters to a Supabase query
 * @param query - Supabase query object
 * @param filters - Filters to apply
 * @returns Modified query
 */
export const applyFilters = (
  query: SupabaseQuery,
  filters: Record<string, unknown> | null | undefined
): SupabaseQuery => {
  if (!filters) return query;

  let currentQuery = query as any;
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null) {
      currentQuery = currentQuery.eq(key, value);
    }
  }

  return currentQuery;
};

/**
 * Apply ordering to a Supabase query
 * @param query - Supabase query object
 * @param orderBy - Order by string
 * @returns Modified query
 */
export const applyOrdering = (
  query: SupabaseQuery,
  orderBy: string | null | undefined
): SupabaseQuery => {
  if (!orderBy) return query;

  const { field, ascending } = parseOrderBy(orderBy);
  return (query as any).order(field, { ascending });
};

/**
 * Apply limit to a Supabase query
 * @param query - Supabase query object
 * @param limit - Limit to apply
 * @returns Modified query
 */
export const applyLimit = (
  query: SupabaseQuery,
  limit: number | null | undefined
): SupabaseQuery => {
  if (!limit) return query;
  return (query as any).limit(limit);
};
